// @ts-nocheck
// cursor: disable-autocomplete
export default {
  'landing.title': 'Welcome to ColQuest',
  common: {
    backToHome: '← Back to Home',
    copyright: '© 2025 ColQuest. Augustin Lafanechere. All rights reserved.',
    loading: 'Loading...',
    price: 'Available on App Stores\nSpring 2025',
    joinWaitlist: {
      line1: '🏔️ Join the Adventure 🚴‍♂️',
      line2: 'Launching Spring 2025'
    },
    privacyPolicy: 'Privacy Policy',
    contact: 'Contact Us',
    submitSummit: 'Submit a Mountain Pass'
  },
  menu: {
    title: 'Menu',
    download: 'Download ColQuest',
    mapper: 'ColQuest Mapper',
    reachOut: 'Contact Us',
    language: 'Language'
  },
  landing: {
    title: 'ColQuest',
    cta: 'Download',
    subtitle: 'Let\'s grow your Col•lection',
    release: {
      coming: 'Available Spring 2025',
      date: 'Join the waitlist for early access',
    },
    roadmap: {
      title: 'Roadmap',
      current: {
        title: 'At Launch',
        features: [
          'iOS and Android apps',
          'Strava synchronization',
          'Mountain pass tracking and discovery',
          'Personal score and ranking',
          'Regional quests and challenges',
        ] as string[],
      },
      future: {
        title: 'Coming Next',
        features: [
          'Additional mountain pass types',
          'Offroad mountain passes',
          'Garmin and other platform integrations',
          'Global and regional leaderboards',
          'Social features and achievements',
        ] as string[],
      }
    },
    waitlist: {
      title: 'Join the Waitlist',
      description: 'Be among the first to try ColQuest!\nBy joining our waitlist, you will:\n• Get early access at launch in your country\n• Receive exclusive updates on development',
      placeholder: 'Enter your email',
      emailLabel: 'Email Address',
      platformLabel: 'Preferred Platform',
      countryLabel: 'Your Country',
      otherCountryPlaceholder: 'Please specify your country',
      button: 'Join the List',
      success: 'Thank you for signing up! We will contact you as soon as ColQuest is available in your country.',
      error: 'Sorry, an error occurred during registration. Please try again.',
    },
    appStores: {
      title: 'Download Now',
      appStore: 'Download on the App Store',
      playStore: 'Get on Google Play',
    },
    download: {
      title: 'Download ColQuest',
      subtitle: 'Start growing your col•lection now!',
      appStore: 'Download on the App Store',
      playStore: 'Download on Google Play',
      androidPreview: 'Join Android Preview',
      join: "Join"
    },
    androidPreview: {
      title: 'Become a Beta-tester',
      description: 'If you\'d like to try ColQuest and help us finalize its official release, you can join our testing team.\n\nIf you purchase a subscription, it will remain valid for future versions.',
      emailLabel: 'Email Address',
      emailPlaceholder: 'Enter your email',
      submit: 'Let\'s go!',
      error: 'Failed to submit request. Please try again.',
      success: {
        title: '👀 Watch your inbox! 📬',
        message: 'Our team will personally reach out to you shortly to provide the installation instructions for our beta version.'
      }
    },
    features: {
      title: 'Features',
      summitTracking: {
        title: 'Track Your Mountain Passes',
        description: 'Automatically detect and log every mountain pass you conquer during your rides.',
      },
      scoringClimbs: {
        title: 'Score Points',
        description: 'Earn points based on difficulty, elevation gain, and new discoveries',
      },
      quests: {
        title: 'Epic Quests',
        description: 'Complete regional quests to earn extra points and give purpose to your rides.',
      },
    },
    hero: {
      title: 'Start hunting for mountain passes!',
      description: 'Grow your col•lection with ColQuest. Every climb counts: accumulate points, complete quests, one mountain pass at a time.',
    },
    howItWorks: {
      title: 'How does it work?',
      step1: {
        title: 'Install the app',
        description: 'Connect your **Strava** or **Garmin** account. ColQuest will load your activities to build your **col•lection**, calculate your score and your progress in quests.'
      },
      step2: {
        title: 'Hunt for mountain passes',
        description: 'After each ascent, ColQuest will **automatically detect the conquered mountain passes** from your activity. The **score** will be calculated based on the **difficulty** of the climb, with **discovery bonuses** or **rarity**.'
      },
      step3: {
        title: 'Complete quests',
        description: 'Mountain passes are organized into **quests**. Complete them to earn extra points and climb the **leaderboard**.'
      },
      faqText: 'Want to know more?',
      faqButton: 'Check our FAQ'
    },
    stats: {
      summits: 'Mountain Passes',
      quests: 'Quests',
      countries: 'Countries',
      submitSummitText: 'Think we\'re missing a mountain pass?',
      submitSummitButton: 'Submit a mountain pass'
    },
    reachOut: {
      title: 'Contact Us',
      instagram: 'Instagram',
      strava: 'Strava',
      email: 'Email'
    },
  },
  faq: {
    title: 'Frequently Asked Questions',
    questions: {
      what: {
        question: 'What is ColQuest?',
        answer: 'ColQuest is a mobile app (iOS and Android) for cyclists who love positive elevation gain. It analyzes your activities from Strava or Garmin to track the mountain passes you have conquered. Each ascent allows you to earn points and climb the rankings. Mountain passes are grouped into quests that allow you to earn extra points when you complete them',
      },
      pricing: {
        question: 'Is ColQuest paid?',
        answer: 'Registration is free.\nAccess to your global score, ranking, and statistics for your future activities is paid, at €34.99 per year, €12.99 per month, or €8.99 per week.',
      },
      summitSelection: {
        question: 'How are mountain passes selected?',
        answer: 'Mountain passes come from the Open Street Map database or are suggested by our users.\nDon\'t hesitate to suggest mountain passes <link>via this form</link>!',
      },
      summitDetection: {
        question: 'How are mountain passes identified in an activity?',
        answer: 'We compare your activity\'s GPS track with the coordinates of mountain passes in the ColQuest database. If your track comes within 200 meters of a mountain pass, that mountain pass is considered conquered.',
      },
      scoring: {
        question: 'How is the score for a mountain pass determined?',
        answer: 'We identify the road taken for the ascent and calculate its gradient and length. These two parameters provide an initial difficulty score. It is then enhanced based on the mountain pass\'s rarity and whether it\'s your first time conquering it. If you complete a quest, you will get 1000 bonus points per mountain pass present in the quest.',
      },
      import: {
        question: 'Can I import my activities?',
        answer: 'We support automatic import of Strava or Garmin activities. Your history is loaded when you sign up. We also want to enable manual import of GPX files directly into the application.',
      },
      rarety: {
        question: 'How is mountain pass rarity determined?',
        answer: 'We analyze daily ascents of each mountain pass by each user to determine the popularity of each mountain pass. The fewer times a mountain pass has been climbed by ColQuest users, the rarer it is. We will adjust rarity thresholds based on the number of users on ColQuest.'
      },
      privacy: {
        question: 'How do you handle my data?',
        answer: 'We only collect activity data shared by Strava or Garmin when you connect your account. This data is exclusively used to power the ColQuest game features (mountain pass detection, scoring, etc.).\n\nYour data and activities remain private and are not shared with other users. You can choose to display your username in the global leaderboard.\n\nFor more details, please read our <privacy>privacy policy</privacy>.'
      }
    },
  },
  privacy: {
    title: 'Privacy Policy',
    intro: {
      title: 'Introduction',
      content: 'This privacy policy explains how we collect, use, disclose, and protect your information when you use our mobile application.',
    },
    collection: {
      title: 'Information We Collect',
      personal: {
        title: 'Information You Provide',
        intro: 'We collect information that you provide directly to us, including:',
        items: [
          'Email address',
          'Country, city, language',
          'Username',
          'Activity data and routes imported from Strava or Garmin',
        ],
      },
      usage: {
        title: 'Information We Collect Automatically',
        intro: 'We automatically collect certain information about your device and your interaction with our application:',
        items: [
          'Device information (model, operating system, etc.)',
          'Connection information',
        ],
      },
    },
    usage: {
      title: 'How We Use Your Information',
      intro: 'We use the information we collect to:',
      items: [
        'Track the mountain passes you have conquered',
        'Calculate your scores and ranking',
        'Recommend quests',
        'Improve mountain pass detection',
        'Enhance the gaming experience',
      ],
    },
    security: {
      title: 'Data Security',
      content: 'We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, modification, disclosure, or destruction.',
    },
    retention: {
      title: 'Data Retention',
      intro: 'We store and retain user data. We keep your personal data for as long as your ColQuest account remains active, because this data is required to operate the app (detecting mountain passes, calculating scores and rankings, recommending quests, and improving the experience). While your account is active, we retain:',
      items: [
        'Account information (email address, username, country, city, language)',
        'Activity data and routes imported from Strava or Garmin',
        'Device and connection information collected automatically',
        'In-app analytics events associated with your account',
      ],
      deletion: 'When you delete your account from the app, we delete your account and the associated personal data from our active systems. If you request deletion via our website, we process the request after verifying your identity, typically within 1 to 2 business days, and in any case within 30 days.',
      backups: 'After account deletion, we do not keep your personal data, except where we must retain limited information for a short period for security, fraud prevention, or legal obligations (for example, encrypted backups that are overwritten on a rolling basis). Such residual copies are deleted according to our backup rotation, generally within 90 days. We do not sell your personal data.',
    },
    contact: {
      title: 'Contact Us',
      content: 'If you have any questions about this privacy policy, please contact us at: ',
    },
    lastUpdated: 'Last updated: August 2026',
    deletion: {
      title: 'Account Deletion',
      content: 'You have the right to request the deletion of your personal data. Upon receiving your request, we will verify your identity and process the deletion of your account and associated data.',
      link: 'Request Account Deletion'
    },
  },
  account_deletion: {
    title: "Account Deletion Request",
    warning: "Warning: Account deletion is permanent and cannot be undone. All your data will be permanently removed from our systems.",
    email_label: "Your Email Address",
    email_placeholder: "Enter your email address",
    submit_button: "Request Deletion",
    error: {
      invalid_email: "Please enter a valid email address",
      submission_failed: "Failed to submit request. Please try again.",
      already_requested: "A deletion request for this email already exists"
    },
    success: {
      title: "Request Received",
      message: "We have received your account deletion request. Our team will contact you at the provided email address to confirm your ownership and process the deletion request. This usually takes 1-2 business days."
    }
  },
  summit_submission: {
    title: 'Submit a Mountain Pass',
    description: 'Help us enrich the ColQuest database by suggesting new mountain passes! Our team will review your submission and may contact you for additional information to ensure the mountain pass meets our criteria.',
    form: {
      name: {
        label: 'Mountain Pass Name *',
        placeholder: 'e.g., Col du Galibier'
      },
      coordinates: {
        title: 'Mountain Pass Location *',
        latitude: {
          label: 'Latitude *',
          placeholder: 'e.g., 45.0633'
        },
        longitude: {
          label: 'Longitude *',
          placeholder: 'e.g., 6.4083'
        }
      },
      email: {
        label: 'Your Email Address *',
        placeholder: 'We may contact you for more details',
        validation: "Please enter a valid email address (e.g., name@example.com)"
      },
      details: {
        label: 'Additional Details (optional)',
        placeholder: 'Tell us about this mountain pass! Why should it be added to ColQuest? Include any relevant information about accessibility, road conditions, or historical significance.'
      },
      submit: 'Submit Mountain Pass'
    },
    success: {
      title: 'Mountain Pass Submitted! 🏔️',
      message: 'Thank you for your contribution! Our team will review your submission and may contact you if we need any additional information.'
    },
    error: {
      invalidEmail: "Please enter a valid email address",
      submission: "An error occurred while submitting the mountain pass. Please try again."
    }
  },
  android_banner: {
    title: 'Download ColQuest',
    subtitle: 'Get the app on Google Play',
    download: 'Download',
    install: 'I have it',
    close_aria: 'Close banner'
  },
  events: {
    loading: 'Loading event...',
    error: {
      title: 'Error Loading Event'
    },
    notFound: {
      title: 'Event Not Found',
      message: 'The event you\'re looking for doesn\'t exist.'
    },
    hero: {
      registerNow: 'Register Now'
    },
    summits: {
      title: 'The Mountain Passes',
      intro: 'This route includes {{count}} mountain pass{{plural}} with a total score of {{totalScore}} points.',
      points: 'pts',
      bonus: 'bonus'
    },
    description: {
      title: 'Description'
    },
    routeOverview: {
      title: 'Details',
      distance: 'km',
      elevation: 'm',
      totalDistance: 'Total Distance',
      elevationGain: 'Elevation Gain',
      minimumScore: 'Minimum ColQuest Score',
      cols: 'Mountain Passes'
    },
    routeVisualization: {
      title: 'The Map'
    }
  },
  premium: {
    title: '✨ ColQuest Premium',
    subtitle: 'Unlock all premium features',
    featuresTitle: 'Included in Premium',
    features: [
      'Automatic scanning of all your future activities',
      'Route planning extension for Strava & Komoot'
    ],
    price: '$12.99',
    priceFrequency: '/year',
    cancelAnytime: 'Cancel anytime',
    subscribeButton: 'Subscribe to Premium',
    processing: 'Processing...',
    noUserIdError: 'No user ID provided. Please sign in first.',
    checkoutError: 'Failed to start checkout',
    successTitle: 'Welcome to Premium!',
    successMessage: 'Your subscription has been activated successfully.',
    extensionBenefit: 'You can now start using all premium features in the ColQuest Mapper extension',
    appBenefit: 'Your premium subscription also works on the mobile app. All your future climbs will be automatically synced.',
    downloadApp: 'Download the ColQuest app to start your adventure:',
    supportNote: 'Need help? Contact us at',
    extensionNote: 'After payment, return to the extension to see your premium features',
    plansTitle: 'Choose your plan'
  },
  mapper: {
    title: 'ColQuest Mapper',
    beta: 'Beta',
    tagline: 'See every col on your route before you ride.',
    cta: 'Add to Chrome',
    ctaNote: 'Requires a ColQuest premium account.',
    howItWorks: {
      title: 'How does it work?',
      step1: {
        title: 'Install the extension',
        description: 'Add ColQuest Mapper to Chrome in one click. Log in with your ColQuest account.'
      },
      step2: {
        title: 'Plan your route',
        description: 'Open Strava or Komoot route builder as usual. Cols appear directly on the map.'
      },
      step3: {
        title: 'Maximize your col\u00B7lection',
        description: 'Adjust your itinerary to ride through as many cols as possible and score big.'
      }
    },
    platforms: {
      title: 'Works with your tools',
      strava: 'Strava Route Builder',
      komoot: 'Komoot Route Planner'
    },
    bottomCta: {
      title: 'Ready to hunt cols?',
      subtitle: 'Start planning smarter rides today.'
    }
  }
}; 